import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Plus, Loader2, ImageIcon, ChevronLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { PostCard } from '../components/blog/PostCard';
import { CreatePostDialog } from '../components/blog/CreatePostDialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProviderBlog() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [providerId, setProviderId] = useState(null);

  useEffect(() => {
    const fetchProviderAndPosts = async () => {
      try {
        // Get provider profile to get provider_id
        const meRes = await fetch(`${API_URL}/api/dashboard/provider`, {
          headers: { 'Authorization': `Bearer ${token || localStorage.getItem('token')}` }
        });
        if (meRes.ok) {
          const data = await meRes.json();
          const pid = data.provider_profile?.provider_id;
          setProviderId(pid);
          
          // Get provider's posts
          const postsRes = await fetch(`${API_URL}/api/posts?provider_id=${pid}`, {
            headers: { 'Authorization': `Bearer ${token || localStorage.getItem('token')}` }
          });
          if (postsRes.ok) setPosts(await postsRes.json());
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchProviderAndPosts();
  }, [token]);

  const handleDelete = async (postId) => {
    if (!window.confirm('Supprimer cette publication ?')) return;
    try {
      const res = await fetch(`${API_URL}/api/posts/${postId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token || localStorage.getItem('token')}` }
      });
      if (res.ok) {
        setPosts(prev => prev.filter(p => p.post_id !== postId));
        toast.success(t('blog.postDeleted'));
      }
    } catch (e) { toast.error(t('common.error')); }
  };

  const handlePostCreated = (newPost) => {
    setPosts(prev => [newPost, ...prev]);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-16 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-24 pb-16" data-testid="provider-blog">
      <div className="container-app max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/provider/dashboard')}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-semibold">{t('blog.myPosts')}</h1>
              <p className="text-muted-foreground text-sm">{posts.length} {posts.length !== 1 ? t('blog.publications') : t('blog.publication')}</p>
            </div>
          </div>
          <Button className="btn-primary" onClick={() => setCreateOpen(true)} data-testid="new-post-btn">
            <Plus className="w-4 h-4 mr-2" />
            {t('blog.newPost')}
          </Button>
        </div>

        {/* Posts Feed */}
        {posts.length === 0 ? (
          <div className="text-center py-16">
            <ImageIcon className="w-16 h-16 text-muted-foreground/20 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-medium mb-2">{t('blog.noPosts')}</h3>
            <p className="text-muted-foreground mb-6">{t('blog.noPostsSubtitle')}</p>
            <Button className="btn-primary" onClick={() => setCreateOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              {t('blog.createFirst')}
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {posts.map(post => (
              <PostCard
                key={post.post_id}
                post={post}
                onDelete={handleDelete}
                showProviderInfo={false}
              />
            ))}
          </div>
        )}

        <CreatePostDialog open={createOpen} onOpenChange={setCreateOpen} onPostCreated={handlePostCreated} />
      </div>
    </div>
  );
}
